import { tt } from './i18n'

/**
 * The map apps a franchise may send its drivers to - `FRONTEND.md` §9.507, §9.509, owner O29/O30.
 *
 * **The owner:** *"add also dropdown where each franchise can choose which map will be default opened from Rozvoz
 * Aplikacia, from these (Google Maps, Apple Maps, Sygic, Waze)"*.
 *
 * **Here rather than in `manager-driving`, because three surfaces need it**: the driving screen builds the link,
 * the delivery settings choose the franchise default and the employee editor chooses a driver's own. §5 forbids
 * `manager-delivery` importing `manager-driving`, so a second copy was the alternative -
 * `agos-one-guard-spelled-twice`. §9.500 moved `isTransientKasaWarning` for the same reason and re-exported it.
 *
 * **The same four `RestaurantSettingsService.MAP_APPS` validates, spelled the same way**, because the value is
 * stored by that setting and read back by it. A cross-stack guard holds the two lists together rather than a
 * comment claiming they agree.
 */
export const MAP_APPS = ['google', 'apple', 'sygic', 'waze', 'mapy'] as const

export type MapApp = (typeof MAP_APPS)[number]

/** The server's default, and what every franchise has until somebody opens the dropdown. */
export const DEFAULT_MAP_APP: MapApp = 'google'

/**
 * `google` for anything unrecognised, mirroring `chosenMapApp`'s own default on the server.
 *
 * Trimmed and lower-cased, so a stored value with stray whitespace still resolves rather than silently becoming
 * the default.
 */
export function chosenMapApp(app: string | null | undefined): MapApp {
  const wanted = (app ?? '').trim().toLowerCase()
  return (MAP_APPS as readonly string[]).includes(wanted) ? (wanted as MapApp) : DEFAULT_MAP_APP
}

/**
 * What each is called in the dropdown.
 *
 * **Product names, so they are not translated** - a driver looking for Waze on their phone is looking for the word
 * *Waze*. `tt` is deliberately not used here, and the lint disable carries that reason.
 */
export const MAP_APP_LABELS: Record<MapApp, string> = {
  // eslint-disable-next-line no-restricted-syntax -- product names, identical in every language
  google: 'Google Maps',
  // eslint-disable-next-line no-restricted-syntax -- product names, identical in every language
  apple: 'Apple Maps',
  // eslint-disable-next-line no-restricted-syntax -- product names, identical in every language
  sygic: 'Sygic',
  // eslint-disable-next-line no-restricted-syntax -- product names, identical in every language
  waze: 'Waze',
  /* §9.512, owner request - Surany asked for this one by name. `mapy.com` is the current domain; the older
   * `mapy.cz` redirects to it. */
  // eslint-disable-next-line no-restricted-syntax -- product names, identical in every language
  mapy: 'Mapy.com',
}

/**
 * Said under the dropdown for the one app that can leave a driver with a dead button - §9.509.
 *
 * Sygic has no web address, so its link opens nothing at all on a phone without the app installed. The other three
 * fall back to a web map. An operator choosing it should be told before a driver finds out.
 */
export function mapAppCaveat(app: MapApp): string | null {
  return app === 'sygic'
    ? tt('Sygic sa otvorí len na telefóne, kde je nainštalovaný. Ostatné otvoria mapu v prehliadači.')
    : null
}
